import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Chaque onglet est une URL distincte : l'onglet courant est déduit de la route active. */
export type CategoriesTab = 'categories' | 'internal' | 'reappro' | 'brands'

/**
 * Source unique des droits de lecture des sous-onglets Catégories.
 *
 * Consommée par le `staticData.permission` de chaque route d'onglet, par la barre d'onglets, et par
 * `/categories` qui n'est accessible que si l'un d'eux l'est. Un seul endroit à mettre à jour : pas
 * de dérive possible entre l'onglet affiché et la page qu'il ouvre.
 */
export const CATEGORIES_TAB_PERMISSIONS = {
  categories: ['read', 'categories.categories.standard'],
  internal: ['read', 'categories.internal-categories.internal'],
  reappro: ['read', 'categories.reappro.category'],
  brands: ['read', 'categories.brands.brand'],
} as const satisfies Record<CategoriesTab, PermissionTuple>

/** Chaque onglet a forcément une route et un droit de lecture — d'où les champs rendus requis. */
type CategoriesTabDefinition = GuardedTab<CategoriesTab> & {
  to: RouteTo
  permission: PermissionTuple
}

/** Ordre de déclaration = ordre d'affichage, et ordre de repli de la redirection `/categories`. */
export const CATEGORIES_TABS: CategoriesTabDefinition[] = [
  {
    value: 'categories',
    label: 'Catégories',
    to: '/categories/categories',
    permission: CATEGORIES_TAB_PERMISSIONS.categories,
  },
  {
    value: 'internal',
    label: 'Catégories internes',
    to: '/categories/internal',
    permission: CATEGORIES_TAB_PERMISSIONS.internal,
  },
  {
    value: 'reappro',
    label: 'Catégories réappro',
    to: '/categories/reappro',
    permission: CATEGORIES_TAB_PERMISSIONS.reappro,
  },
  {
    value: 'brands',
    label: 'Marques',
    to: '/categories/brands',
    permission: CATEGORIES_TAB_PERMISSIONS.brands,
  },
]

/**
 * Accès au module : dès qu'un seul de ses écrans est accessible — les créations et la configuration
 * réappro comprises, faute de quoi un rôle `add`/`edit` voit l'entrée de menu et se fait refuser
 * l'index (audit du 19/09, MAJ-07).
 */
export const CATEGORIES_PAGE_READ: PermissionRequirement = moduleIndexPermission([
  ...CATEGORIES_TABS.map((tab) => tab.permission),
  ['add', 'categories.categories.standard'],
  ['add', 'categories.brands.brand'],
  ['edit', 'categories.brands.brand'],
  ['read', 'categories.reappro.config'],
])

/**
 * Écran « Configuration réappro » : il tient à la fois les stocks minimum d'une boutique
 * (`categories.reappro.config`) et la matrice des algorithmes, passée sous sa ressource propre par
 * l'audit du 19/09 (`categories.reappro.algorithm`, TAX-M12). L'un OU l'autre l'ouvre, chaque bloc
 * gardant ensuite sa ressource.
 */
export const REAPPRO_CONFIG_PAGE_READ: PermissionRequirement = moduleIndexPermission([
  ['read', 'categories.reappro.config'],
  ['read', 'categories.reappro.algorithm'],
])
